import React, { useEffect, useState } from "react";
import { PublicShell } from "@/components/PublicShell";
import { MapPin, Phone, Mail, Building2 } from "lucide-react";
import { api } from "@/lib/api";

export default function Contact() {
  const [offices, setOffices] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/content/offices").then((r) => {
      setOffices(r.data);
      if (r.data.length) setSelected(r.data[0]);
    });
  }, []);

  const mapQuery = (o) => {
    if (o?.map_query) return o.map_query;
    if (o?.lat && o?.lng) return `${o.lat},${o.lng}`;
    return encodeURIComponent(`${o?.address || ""} ${o?.city || ""} ${o?.postcode || ""}`);
  };

  const embedSrc = () => {
    if (!selected) return "";
    // Free Google Maps embed (no API key required)
    return `https://www.google.com/maps?q=${mapQuery(selected)}&z=15&output=embed`;
  };

  return (
    <PublicShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline text-secondary">Contact us</div>
          <h1 className="mt-2 font-display text-5xl font-light tracking-tight">Come say hi</h1>
          <p className="mt-4 text-muted-foreground">
            We're a UK-based team with offices in London, Manchester, and Birmingham. General enquiries go to <a href="mailto:info@treasurescare.com" className="text-primary hover:underline">info@treasurescare.com</a>.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="contact-offices">
          {offices.map((o) => (
            <button
              key={o.id}
              onClick={() => setSelected(o)}
              data-testid={`office-card-${o.id}`}
              className={`text-left rounded-2xl border p-6 transition-all ${
                selected?.id === o.id
                  ? "border-primary shadow-teal-glow bg-card"
                  : "border-border bg-card hover:border-primary/50"
              }`}
            >
              <div className="flex items-center gap-2 text-secondary">
                <Building2 className="w-4 h-4" />
                <div className="font-medium">{o.name}</div>
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    {o.address}<br />
                    {o.city} {o.postcode}
                  </div>
                </div>
                {o.phone && (
                  <div className="flex gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4 mt-0.5 shrink-0" />
                    <a href={`tel:${o.phone}`} className="hover:text-foreground">{o.phone}</a>
                  </div>
                )}
                {o.email && (
                  <div className="flex gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                    <a href={`mailto:${o.email}`} className="hover:text-foreground">{o.email}</a>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-2xl overflow-hidden border border-border" data-testid="contact-map">
          {selected ? (
            <iframe
              key={selected.id}
              title={`Map — ${selected.name}`}
              src={embedSrc()}
              className="w-full h-[420px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          ) : (
            <div className="h-[420px] bg-muted flex items-center justify-center text-muted-foreground">
              Loading map…
            </div>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
